import React, { useCallback } from 'react';
import { defineMessages, useIntl, type MessageValue } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, getCloudinaryUrl, Image, Radio, theme } from '@sg/garnish';

import { INGREDIENT_CARD_IMAGE_WIDTH } from '../../../../constants';
import { CustomizationIngredientNotice } from '../../../CustomizationIngredientNotice';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationDetailsDressingPortionV2 = (props: Props) => {
  const {
    ingredient,
    selectedPortion,
    maxPortions,
    remainingPortions,
    isDisabled,
    size = 'md',
    restrictionNotice,
    restrictionNoticeVariation = 'icon',
    restrictionModalTitle,
    restrictionModalButtonTitle,
    setDressingPortion,
  } = props;

  const { formatMessage } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  const { id, asset, name } = ingredient;

  const formattedImageUrl = getCloudinaryUrl(asset.url, { crop: 'scale', width: INGREDIENT_CARD_IMAGE_WIDTH }); // prettier-ignore

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onPortionChange = useCallback(
    (portionAsString: string) => {
      setDressingPortion(id, Number(portionAsString));
    },
    [id, setDressingPortion],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Image source={formattedImageUrl} style={size === 'md' ? styles.imageMD : styles.imageLG} />

        <BodyText sizeMatch={['18']} style={styles.heading}>
          {name}
        </BodyText>

        {restrictionNotice ? (
          <CustomizationIngredientNotice
            notice={restrictionNotice}
            ingredientName={name}
            variation={restrictionNoticeVariation}
            restrictionModalTitle={restrictionModalTitle}
            restrictionModalButtonTitle={restrictionModalButtonTitle}
          />
        ) : null}
      </View>

      <Radio.Group gap={size === 'md' ? theme.spacing['2'] : theme.spacing['4']} itemsPerRow={3}>
        {Array.from({ length: maxPortions }).map((_value, index) => {
          const portion = index + 1;
          const moreThanRemaining = portion > selectedPortion + remainingPortions;

          return (
            <Radio.Item
              key={`dressing-portion-${portion}`}
              value={`${portion}`}
              size={size === 'lg' ? 'md' : 'sm'}
              isSelected={selectedPortion === portion}
              isDisabled={isDisabled || moreThanRemaining}
              onPress={onPortionChange}
              label={
                portion === 1
                  ? formatMessage(messages.labelSingular, { portion })
                  : formatMessage(messages.labelPlural, { portion })
              }
              palette="spinach"
            />
          );
        })}
      </Radio.Group>
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly labelSingular: { readonly portion: MessageValue };
  readonly labelPlural: { readonly portion: MessageValue };
}>({
  labelSingular: {
    defaultMessage: '{portion} portion',
    description: 'Customization | Dressing details | Dressing portion label | Singular',
  },
  labelPlural: {
    defaultMessage: '{portion} portions',
    description: 'Customization | Dressing details | Dressing portion label | Plural',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    rowGap: theme.spacing['1'],
  },
  header: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: theme.spacing['2'],
    rowGap: theme.spacing['1'],
    marginBottom: theme.spacing['2'],
  },
  imageMD: {
    width: 40,
    height: 40,
    borderRadius: 40,
  },
  imageLG: {
    width: 64,
    height: 64,
    borderRadius: 64,
  },
  heading: {
    color: theme.colors.KALE,
    flexShrink: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  ingredient: { id: string; name: string; asset: { url: string } };
  selectedPortion: number;
  maxPortions: number;
  remainingPortions: number;
  isDisabled?: boolean;
  size?: 'md' | 'lg';
  restrictionNotice?: string;
  restrictionNoticeVariation?: 'label' | 'icon';
  restrictionModalTitle?: string;
  restrictionModalButtonTitle?: string;
  setDressingPortion: (ingredientId: string, dressingPortion: number) => void;
};
